import { Step } from 'react-joyride';

export interface CustomStep extends Step {
  data?: {
    route?: string;
    [key: string]: any;
  };
}

/**
 * Builds concise, highly-targeted guided tour steps for Organization Admins
 */
export const getOrgAdminSteps = (sharkName: string): CustomStep[] => [
  // ── MODULE 1: DASHBOARD ──
  {
    target: 'body',
    placement: 'center',
    title: `Welcome to FINN AI! 👋`,
    content: `Hi, I'm ${sharkName}! Let's take a quick guided tour across all modules in your workspace.`,
    skipBeacon: true,
    data: { route: '/admin/dashboard' },
  },
  {
    target: '#finn-tour-extension',
    placement: 'bottom',
    title: `Extension Setup`,
    content: `Install the Chrome Extension for live call battlecards and AI meeting assistance.`,
    data: { route: '/admin/dashboard' },
  },

  // ── MODULE 2: TEAM MANAGEMENT ──
  {
    target: '#tour-invite-member',
    placement: 'bottom-start',
    title: `Invite Team Reps`,
    content: `Click here to invite sales reps and assign workspace permissions.`,
    data: { route: '/admin/team' },
  },
  {
    target: '#tour-member-list',
    placement: 'bottom-start',
    title: `Team Roster`,
    content: `Monitor active reps, assigned roles, and seat licenses.`,
    data: { route: '/admin/team' },
  },

  // ── MODULE 3: KNOWLEDGE BASE ──
  {
    target: '#tour-kb-upload',
    placement: 'bottom-start',
    title: `Upload Assets`,
    content: `Upload product specs, pricing guides, and competitor battlecards.`,
    data: { route: '/admin/knowledge' },
  },
  {
    target: '#tour-kb-controls',
    placement: 'bottom-start',
    title: `Search & Categories`,
    content: `Filter and search indexed documents used for live AI call answers.`,
    data: { route: '/admin/knowledge' },
  },

  // ── MODULE 4: PRE-CALL INTEL ──
  {
    target: '#tour-precall-calendars',
    placement: 'bottom-start',
    title: `Calendar Connections`,
    content: `Sync Google or Outlook Calendar for automated meeting briefings.`,
    data: { route: '/admin/pre-call' },
  },
  {
    target: '#tour-precall-dossier',
    placement: 'bottom-start',
    title: `Briefing Dossiers`,
    content: `Review attendee backgrounds, company news, and talking points before calls.`,
    data: { route: '/admin/pre-call' },
  },

  // ── MODULE 5: RECENT CALLS ──
  {
    target: '#tour-calls-filters',
    placement: 'bottom-start',
    title: `Filter Recordings`,
    content: `Filter call recordings by team member, date range, or search keywords.`,
    data: { route: '/admin/calls' },
  },
  {
    target: '#tour-calls-table',
    placement: 'bottom-start',
    title: `Transcripts & Analytics`,
    content: `Inspect AI call transcripts, speaker sentiment, and buying intent signals.`,
    data: { route: '/admin/calls' },
  },

  // ── MODULE 6: SHARK SETTINGS ──
  {
    target: '#tour-shark-select',
    placement: 'bottom-start',
    title: `Companion Customization`,
    content: `Customize your shark companion's species avatar and costume outfits.`,
    data: { route: '/admin/copilot-settings' },
  },

  // ── MODULE 7: INTEGRATIONS ──
  {
    target: '#tour-integrations-cards',
    placement: 'bottom-start',
    title: `CRM & Calendar Sync`,
    content: `Connect Google, Outlook, Salesforce, and HubSpot for automatic 1-click sync.`,
    data: { route: '/admin/integrations' },
  },

  // ── CONCLUSION ──
  {
    target: 'body',
    placement: 'center',
    title: `All Set! 🎉`,
    content: `You're ready to use FINN AI! Install the extension and start winning more calls.`,
    data: { route: '/admin/dashboard' },
  }
];

/**
 * Builds concise guided tour steps for Team Members / Reps
 */
export const getMemberSteps = (sharkName: string): CustomStep[] => [
  // ── MODULE 1: DASHBOARD ──
  {
    target: 'body',
    placement: 'center',
    title: `Welcome to FINN AI! 👋`,
    content: `Hi, I'm ${sharkName}! Let's tour your sales copilot workspace.`,
    skipBeacon: true,
    data: { route: '/admin/dashboard' },
  },
  {
    target: '#finn-tour-extension',
    placement: 'bottom',
    title: `Chrome Extension`,
    content: `Install the extension for live meeting assistance and battlecard popups.`,
    data: { route: '/admin/dashboard' },
  },

  // ── MODULE 2: PRE-CALL INTEL ──
  {
    target: '#tour-precall-dossier',
    placement: 'bottom-start',
    title: `Pre-Call Briefs`,
    content: `View prospect profiles and key insights before getting on calls.`,
    data: { route: '/admin/pre-call' },
  },

  // ── MODULE 3: RECENT CALLS ──
  {
    target: '#tour-calls-table',
    placement: 'bottom-start',
    title: `Call Transcripts`,
    content: `Review recorded meetings, searchable transcripts, and action items.`,
    data: { route: '/admin/calls' },
  },

  // ── MODULE 4: SHARK SETTINGS ──
  {
    target: '#tour-shark-select',
    placement: 'bottom-start',
    title: `Companion Avatar`,
    content: `Customize your shark companion's nickname and avatar outfit.`,
    data: { route: '/admin/copilot-settings' },
  },

  // ── MODULE 5: INTEGRATIONS ──
  {
    target: '#tour-integrations-cards',
    placement: 'bottom-start',
    title: `Calendar Sync`,
    content: `Connect your calendar to sync notes and meetings automatically.`,
    data: { route: '/admin/integrations' },
  },

  // ── CONCLUSION ──
  {
    target: 'body',
    placement: 'center',
    title: `All Set! 🎉`,
    content: `You're ready! Start your next call with FINN AI enabled.`,
    data: { route: '/admin/dashboard' },
  }
];
